import { MonitoringFailureReason } from '../monitoring-anomaly.model';
import { MonitoringSystemDetail } from '../monitoring-detection.service';
import { AnomalyChecker } from './anomaly-checker.interface';

/**
 * Anomaly checker for Microsoft Clarity
 */
export class ClarityAnomalyChecker implements AnomalyChecker {
  readonly key = 'clarity';
  readonly name = 'Clarity';

  check(): MonitoringSystemDetail {
    const result: MonitoringSystemDetail = {
      isActive: false,
      scriptLoaded: false,
      lastChecked: Date.now(),
    };

    try {
      if (typeof window === 'undefined') {
        result.failureReason = MonitoringFailureReason.UNKNOWN;
        return result;
      }

      // Check if script tag exists (Clarity script URL)
      const scriptTag = document.querySelector('script[src*="clarity.ms"]');
      result.scriptLoaded = scriptTag !== null;

      const clarity = (window as any).clarity;

      // Check if clarity function exists and is callable
      if (typeof clarity === 'function') {
        // Verify Clarity is actually working by checking if we can call it
        try {
          // Clarity provides a method to check if it's initialized
          // The clarity function should be available if loaded properly
          clarity('consent'); // This is a safe no-op call
          result.isActive = true;
          return result;
        } catch (clarityError) {
          // Clarity function exists but throws error when called
          result.isActive = false;
          result.failureReason = MonitoringFailureReason.NOT_INITIALIZED;
          return result;
        }
      }

      // Additional check: Look for Clarity's global state
      // Clarity sets window.clarity as a function when properly loaded
      const hasClarityQueue = Array.isArray((window as any).clarity?.q);
      if (hasClarityQueue) {
        // Clarity is loading but not yet initialized
        result.isActive = false;
        result.failureReason = MonitoringFailureReason.NOT_INITIALIZED;
        return result;
      }

      // Determine failure reason
      if (!result.scriptLoaded) {
        result.failureReason = MonitoringFailureReason.NOT_LOADED;
      } else if (result.scriptLoaded && !clarity) {
        // Script loaded but clarity object doesn't exist - likely blocked
        result.failureReason = MonitoringFailureReason.BLOCKED;
      } else {
        result.failureReason = MonitoringFailureReason.UNKNOWN;
      }

      return result;
    } catch (error) {
      console.error(
        `[MonitoringDetection] Error checking ${this.name}:`,
        error,
      );
      result.failureReason = MonitoringFailureReason.UNKNOWN;
      return result;
    }
  }
}
